import CenteredSpinner from '@app/CenteredSpinner';
import { router } from '@app/routes';
import Unauthenticated from '@app/Unauthenticated';
import { OrganizationOnboarding } from './Onboarding/OrganizationOnboarding';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  EmptyStateVariant,
  PageSection,
} from '@patternfly/react-core';
import { ExclamationTriangleIcon, SignOutAltIcon, RedoIcon } from '@patternfly/react-icons';
import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { returnToSigninArgs } from '../utils/returnTo';
import { RouterProvider } from 'react-router-dom';
import { useApiFetch } from '../api';
import { NotificationProvider } from '../contexts/NotificationContext';
import { RolesProvider } from '../contexts/roles/RolesContext';
import { useSetUserContext, useUserContext } from '../contexts/user/UserContext';
import { ApiResponseUser } from '../types/api/ApiResponseUser';

export const AuthenticatedApp: React.FC = () => {
  const setUserContext = useSetUserContext();
  const user = useUserContext();
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  // Track whether we've completed initial user data fetch
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  // Prevent duplicate fetch calls during strict mode or rapid re-renders
  const fetchInProgress = useRef(false);

  const fetchUserData = async () => {
    if (auth.isAuthenticated && auth.user) {
      // Prevent duplicate concurrent fetches
      if (fetchInProgress.current) {
        return;
      }
      fetchInProgress.current = true;
      setIsRetrying(true);
      setFetchError(null);

      try {
        const response = await apiFetch(`/users/${auth.user.profile.sub}`);

        if (response.ok) {
          const apiResponse: ApiResponseUser = await response.json();
          if (apiResponse.status === 'success') {
            setUserContext(apiResponse.data.user);
            console.log('Successfully loaded user');
          }
        } else {
          let errorMessage = 'An unexpected error occurred';

          switch (response.status) {
            case 401:
              errorMessage = 'Your session has expired. Please log in again.';
              auth.signinRedirect(returnToSigninArgs());
              break;
            case 403:
              errorMessage = 'You do not have permission to access this resource';
              break;
            case 404:
              errorMessage = 'User profile not found';
              break;
            case 500:
              errorMessage = 'Internal server error. Please try again later';
              break;
            default:
              errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
          console.error('Error fetching user data:', response);
          setFetchError(errorMessage);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setFetchError('Network error. Please check your connection and try again.');
      } finally {
        setIsRetrying(false);
        setIsInitialLoading(false);
        fetchInProgress.current = false;
      }
    }
  };

  useEffect(() => {
    // Only fetch if authenticated, and we haven't successfully loaded user yet
    if (auth.isAuthenticated && auth.user && user === null && !fetchError) {
      fetchUserData();
    }
    // If not authenticated, ensure we're not stuck in loading state
    if (!auth.isAuthenticated && !auth.isLoading) {
      setIsInitialLoading(false);
    }
  }, [auth.isAuthenticated, auth.user, auth.isLoading]);

  const handleRetry = () => {
    fetchUserData();
  };

  const handleLogout = () => {
    auth.signoutRedirect();
  };

  // Show spinner while auth is loading
  if (auth.isLoading) {
    return <CenteredSpinner />;
  }

  // Show auth error if authentication failed
  if (auth.error) {
    return (
      <PageSection>
        <EmptyState
          variant={EmptyStateVariant.lg}
          status="danger"
          headingLevel="h1"
          icon={ExclamationTriangleIcon}
          titleText="Authentication Error"
        >
          <EmptyStateBody>
            <Content>
              <p>
                We encountered a problem during authentication: <strong>{auth.error.message}</strong>
              </p>
              <p>This may be a temporary issue with the authentication service. Please try logging in again.</p>
            </Content>
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                icon={<SignOutAltIcon />}
                onClick={() => void auth.signinRedirect(returnToSigninArgs())}
              >
                Return to Login
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </PageSection>
    );
  }

  // Show unauthenticated screen if not logged in
  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Show spinner during initial user data fetch (prevents flash of error state)
  if (isInitialLoading) {
    return <CenteredSpinner />;
  }

  // Show fetch error if we have one (only after initial loading completes)
  if (fetchError) {
    return (
      <PageSection>
        <EmptyState
          variant={EmptyStateVariant.lg}
          status="warning"
          headingLevel="h1"
          icon={ExclamationTriangleIcon}
          titleText="Unable to Load Profile"
        >
          <EmptyStateBody>
            <Content>
              <p>{fetchError}</p>
              <p>
                Your authentication was successful, but we couldn't retrieve your user profile from the server. This
                might be a temporary connectivity issue.
              </p>
            </Content>
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                icon={<RedoIcon />}
                onClick={handleRetry}
                isLoading={isRetrying}
                isDisabled={isRetrying}
              >
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </Button>
            </EmptyStateActions>
            <EmptyStateActions>
              <Button variant="link" icon={<SignOutAltIcon />} onClick={handleLogout}>
                Sign Out
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </PageSection>
    );
  }

  // Show service unavailable only if user is null AFTER initial loading completed without error
  if (user === null) {
    return (
      <PageSection>
        <EmptyState
          variant={EmptyStateVariant.lg}
          status="danger"
          headingLevel="h1"
          icon={ExclamationTriangleIcon}
          titleText="Service Unavailable"
        >
          <EmptyStateBody>
            <Content>
              <p>We're having trouble connecting to the Ancalagon Manager services.</p>
              <p>
                You've been successfully authenticated, but the backend services are currently unavailable. This is
                usually a temporary issue — please wait a moment and try again.
              </p>
            </Content>
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                icon={<RedoIcon />}
                onClick={handleRetry}
                isLoading={isRetrying}
                isDisabled={isRetrying}
              >
                {isRetrying ? 'Connecting...' : 'Retry Connection'}
              </Button>
            </EmptyStateActions>
            <EmptyStateActions>
              <Button variant="link" icon={<SignOutAltIcon />} onClick={handleLogout}>
                Sign Out
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </PageSection>
    );
  }

  // Gate non-active users (clientless / join_pending) to the onboarding flow. This renders
  // INSTEAD OF <RouterProvider> (i.e. instead of AppLayout) — the same seam as the
  // <Unauthenticated /> gate above. `user` is guaranteed non-null here (the `user === null`
  // guard returned above), so reading `onboarding_state` is safe. `fetchUserData` is passed
  // as the canonical refresh primitive for the create/join/withdraw screens (Steps 3–5).
  if (user.onboarding_state !== 'active') {
    return <OrganizationOnboarding user={user} refreshUser={fetchUserData} />;
  }

  return (
    <RolesProvider>
      <NotificationProvider>
        <RouterProvider router={router}></RouterProvider>
      </NotificationProvider>
    </RolesProvider>
  );
};
